import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  PenTool,
  Eye,
  Trash2,
  Download,
  FileSignature,
  UploadCloud,
} from "lucide-react";
import { API_URL } from "../../api/api";
import { UPLOAD_URL } from "../../api/api";
import api from "../../api/axios";
import {
  T,
  Page,
  PageHeader,
  Card,
  PrimaryButton,
  SecondaryButton,
  SearchInput,
  Select,
  IconButton,
  Pagination,
  EmptyState,
} from "../../components/em/ui";
import SignatureCsvModal from "./SignatureCsvModal";

import { confirmDialog } from "../../components/em/confirm";
import { toast } from "../../utils/toast";
const PER_PAGE = 20;

const SignaturesManagement = () => {
  const navigate = useNavigate();
  // Module is reachable under /admin and /hr — link within the same base.
  const basePath =
    (localStorage.getItem("role") || "").toLowerCase().trim() === "hr"
      ? "/hr"
      : "/admin";

  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [designation, setDesignation] = useState("");
  const [csvOpen, setCsvOpen] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchSignatures();
  }, []);

  const fetchSignatures = async () => {
    setLoading(true);
    try {
      const res = await api.get("/signatures");
      setSignatures(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching signatures:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sig) => {
    if (!(await confirmDialog(
      `Delete the signature for ${sig.name || "this person"}? This cannot be undone.`
    )))
      return;
    try {
      await api.delete(`/signatures/${sig.id}`);
      fetchSignatures();
    } catch (err) {
      console.error("Error deleting signature:", err);
      toast.error("Error deleting signature");
    }
  };

  // Distinct designations for the filter dropdown (sorted, de-duped).
  const designations = useMemo(
    () =>
      Array.from(
        new Set(
          signatures.map((s) => (s.designation || "").trim()).filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [signatures],
  );

  // Client-side filtering: search across name / designation / phone, then the
  // designation dropdown narrows to an exact designation.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return signatures.filter((s) => {
      if (designation && (s.designation || "") !== designation) return false;
      if (!q) return true;
      return [s.name, s.designation, s.phone]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [signatures, query, designation]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageRows = filtered.slice(
    (safePage - 1) * PER_PAGE,
    safePage * PER_PAGE,
  );

  const onSearch = (e) => {
    setQuery(e.target.value);
    setPage(1);
  };

  const th = {
    textAlign: "left",
    padding: "11px 16px",
    font: `600 11px/1.4 ${T.font}`,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: T.fg5,
    borderBottom: `1px solid ${T.border}`,
    whiteSpace: "nowrap",
  };
  const td = {
    padding: "12px 16px",
    font: `400 13px/1.4 ${T.font}`,
    color: T.fg1,
    borderBottom: `1px solid ${T.border}`,
    verticalAlign: "middle",
  };

  return (
    <Page>
      <PageHeader
        icon={PenTool}
        title="Manage Signatures"
        subtitle="Create, preview and download email signatures"
        actions={
          <PrimaryButton
            icon={PenTool}
            onClick={() => navigate(`${basePath}/signature/add`)}
          >
            Add Signature
          </PrimaryButton>
        }
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ width: 320, maxWidth: "100%" }}>
          <SearchInput
            value={query}
            onChange={onSearch}
            placeholder="Search by name, designation or phone…"
          />
        </div>

        {/* Filter + bulk upload pushed to the far right */}
        <div style={{ flex: 1 }} />

        <Select
          value={designation}
          onChange={(e) => {
            setDesignation(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by designation"
          style={{ width: "auto", minWidth: 190 }}
        >
          <option value="">All designations</option>
          {designations.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>

        <SecondaryButton icon={UploadCloud} onClick={() => setCsvOpen(true)}>
          Bulk Upload
        </SecondaryButton>
      </div>

      <Card padding={0} style={{ overflow: "hidden" }}>
        {loading ? (
          <div
            style={{
              padding: "56px 24px",
              textAlign: "center",
              font: `400 13px ${T.font}`,
              color: T.fg5,
            }}
          >
            Loading signatures…
          </div>
        ) : pageRows.length === 0 ? (
          <EmptyState
            icon={FileSignature}
            title={
              query || designation
                ? "No matching signatures"
                : "No signatures yet"
            }
            subtitle={
              query || designation
                ? "Try a different search or designation filter."
                : "Create your first email signature to get started."
            }
            action={
              !query &&
              !designation && (
                <PrimaryButton
                  icon={PenTool}
                  onClick={() => navigate(`${basePath}/signature/add`)}
                >
                  Add Signature
                </PrimaryButton>
              )
            }
          />
        ) : (
          <div style={{ overflowX: "auto" }} className="signatureTable">
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 720,
              }}
            >
              <thead>
                <tr style={{ background: T.bgHead }}>
                  <th style={th}>Name</th>
                  <th style={th}>Designation</th>
                  <th style={th}>Phone</th>
                  <th style={th}>Signature</th>
                  <th style={{ ...th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((sig) => (
                  <tr key={sig.id}>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {sig.name || "—"}
                    </td>
                    <td style={{ ...td, color: T.fg5 }}>
                      {sig.designation || "—"}
                    </td>
                    <td style={{ ...td, color: T.fg5 }}>{sig.phone || "—"}</td>
                    <td style={td}>
                      {sig.image_path ? (
                        <a
                          href={`${API_URL}/api/signatures/${sig.id}/download`}
                          title="Download signature"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <img
                            src={`${UPLOAD_URL}${sig.image_path}`}
                            alt={`${sig.name} signature`}
                            style={{
                              height: 38,
                              width: 132,
                              objectFit: "contain",
                              borderRadius: T.rSm,
                              border: `1px solid ${T.border}`,
                              background:
                                sig.theme === "black" ? "#0a0a0a" : "#fff",
                              padding: 2,
                            }}
                          />
                        </a>
                      ) : (
                        <span style={{ color: T.fg7 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...td }}>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          justifyContent: "flex-end",
                        }}
                      >
                        <a
                          href={`${API_URL}/api/signatures/${sig.id}/download`}
                          title="Download"
                        >
                          <IconButton icon={Download} title="Download" />
                        </a>
                        <IconButton
                          icon={Eye}
                          title="View"
                          onClick={() =>
                            navigate(
                              `${basePath}/signature/signature/${sig.id}`,
                            )
                          }
                        />
                        <IconButton
                          icon={Trash2}
                          tone="danger"
                          title="Delete"
                          onClick={() => handleDelete(sig)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Pagination
        page={safePage}
        totalPages={totalPages}
        total={filtered.length}
        pageSize={PER_PAGE}
        onPage={setPage}
      />

      <SignatureCsvModal
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        onImported={fetchSignatures}
      />
    </Page>
  );
};

export default SignaturesManagement;
